import React, { useRef } from 'react'
import Collection from '@jimmyboys/liita-ui/dist/components/Collection'

import NoData from 'components/general/NoData/NoData'

import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import { LINE_SPLIT_REGEX } from 'controllers/ClinicalProcess/utils'
import useRect from '@jimmyboys/liita-ui/dist/hooks/useRect'
import useCalculateTextsHeights from '@jimmyboys/liita-ui/dist/hooks/useCalculateTextsHeights'
import NotificationCard, {
	NotificationCardSkeleton,
} from 'components/cards/NotificationCard/NotificationCard'
import ExpandableWidget from 'components/ExpandableWidget/ExpandableWidget'

import themeStyles from 'assets/styles/theme.module.sass'
import useCollectionSpecs, {
	getThemeSpacing,
	getThemeVariable,
} from 'hooks/useCollectionSpecs'

import styles from './NotificationsView.module.sass'

const { phi, smallFontSize } = themeStyles
const [themeVerticalSpacing, themeHorizontalSpacing] = getThemeSpacing()
const themeSmallFontSize = getThemeVariable(smallFontSize)

const NotificationsView = (props) => {
	const { i18n } = useGlobal()

	const {
		data: notificationsData,
		processId,
		processData: notificationsForProcess,
		state,
		className,
		...attributes
	} = props
	const collectionRef = useRef(null)
	const rect = useRect(collectionRef)

	const cards = {
		NotificationCard,
		NotificationCardSkeleton,
	}

	const cardLabels = {
		options: i18n`Options`,
		unread: i18n`Mark as Unread`,
		read: i18n`Mark as Read`,
		reject: i18n`Close`,
		postpone: i18n`Postpone`,
		reply: i18n`Reply`,
		// Removed forward to backoffice, because backoffice is to be removed
		// forward: i18n`Forward to manager`,
	}

	const data = (notificationsData || []).map((json) => {
		return {
			...json,
			$cardName: 'NotificationCard',
			$applyCardBackground: false,
			$cardLabels: cardLabels,
		}
	})

	const isLoading = state === 'loading'

	const textSpecs = useCollectionSpecs(
		collectionRef,
		rect,
		themeHorizontalSpacing,
		themeSmallFontSize
	)
	const cardMargin = themeVerticalSpacing / phi / phi

	const isBeforeTextComputation = !(textSpecs.fontSize && textSpecs.maxWidth)

	const textsHeights = useCalculateTextsHeights(
		isBeforeTextComputation ? [] : data,
		textSpecs.fontSize
			? `${textSpecs.fontWeight} ${textSpecs.fontSize}px ${
					textSpecs.fontFamily || ''
			  }`
			: null,
		textSpecs.letterSpacing,
		textSpecs.maxWidth,
		LINE_SPLIT_REGEX
	)

	const textsHeightsForProcess = useCalculateTextsHeights(
		isBeforeTextComputation ? [] : notificationsForProcess,
		textSpecs.fontSize
			? `${textSpecs.fontWeight} ${textSpecs.fontSize}px ${
					textSpecs.fontFamily || ''
			  }`
			: null,
		textSpecs.letterSpacing,
		textSpecs.maxWidth,
		LINE_SPLIT_REGEX
	)

	let dataForProcess = []
	if (processId && notificationsForProcess) {
		dataForProcess = (notificationsForProcess || []).map((json) => {
			return {
				...json,
				$cardName: 'NotificationCard',
				$applyCardBackground: false,
				$cardLabels: cardLabels,
			}
		})
	}

	let itemsHeights = []
	let collectionItems = []
	let processItemsHeights = []
	let collectionItemsForProcess = []
	if (isLoading) {
		const loadingItems = new Array(5).fill({})
		collectionItems = loadingItems.map(() => {
			return {
				$cardName: 'NotificationCardSkeleton',
			}
		})
		itemsHeights = loadingItems.map(() => {
			return (
				NotificationCardSkeleton.defaultHeight +
				(parseFloat(styles.rowMargin) || cardMargin)
			)
		})
		if (processId) {
			collectionItemsForProcess = loadingItems.map(() => {
				return {
					$cardName: 'NotificationCardSkeleton',
					$applyCardBackground: false,
				}
			})

			processItemsHeights = itemsHeights
		}
	} else {
		data.forEach((item) => {
			const numberOfLines =
				item && textsHeights && textsHeights[item.textId]
					? textsHeights[item.textId] + 2
					: 0
			const lineHeight = textSpecs.lineHeight || 1
			const cardDefaultLines = NotificationCard.defaultLines
			const extraLines = Math.max(0, numberOfLines - cardDefaultLines)
			const extraHeight = extraLines * lineHeight
			const cardHeight = NotificationCard.defaultHeight
			const textHeight = numberOfLines * lineHeight
			const height = cardHeight + cardMargin + extraHeight
			itemsHeights.push(height)
			collectionItems.push({
				...item,
				height,
				textHeight,
			})
		})

		if (processId) {
			dataForProcess.forEach((item) => {
				const numberOfLines =
					item &&
					textsHeightsForProcess &&
					textsHeightsForProcess[item.textId]
						? textsHeightsForProcess[item.textId] + 2
						: 0
				const lineHeight = textSpecs.lineHeight || 1
				const cardDefaultLines = NotificationCard.defaultLines
				const extraLines = Math.max(0, numberOfLines - cardDefaultLines)
				const extraHeight = extraLines * lineHeight
				const cardHeight = NotificationCard.defaultHeight
				const textHeight = numberOfLines * lineHeight
				const height = cardHeight + cardMargin + extraHeight
				processItemsHeights.push(height)
				collectionItemsForProcess.push({
					...item,
					height,
					textHeight,
				})
			})
		}
	}

	return (
		<div
			{...attributes}
			className={`${styles['notifications-container']} ${
				className || ''
			}`}
		>
			{processId && (
				<ExpandableWidget
					className={styles['aside-widget']}
					titleAppearance='aside-widget'
					arrowPosition='right'
					heading='small'
					title={i18n`Notifications for process ${processId}`}
					collapsedLabel={collectionItemsForProcess.length}
					expandedLabel={collectionItemsForProcess.length}
					expanded={!!collectionItemsForProcess.length}
					expandable={!!collectionItemsForProcess.length}
					borderRadius
					margins={['small', '', '']}
					paddings={['small', '', '']}
					applyMaxWidth
				>
					{!isLoading && !collectionItemsForProcess.length ? (
						<NoData
							applyMaxWidth
							paddings={['normal', 'normal']}
							message={i18n`You have no **notifications**.`}
						/>
					) : null}
					<Collection
						className={styles['list']}
						items={collectionItemsForProcess}
						renderAhead={isBeforeTextComputation ? 0 : 10}
						itemsHeights={processItemsHeights}
						cards={cards}
					/>
				</ExpandableWidget>
			)}
			<ExpandableWidget
				className={styles['aside-widget']}
				titleAppearance='aside-widget'
				arrowPosition='right'
				heading='small'
				title={i18n`Other Notifications`}
				collapsedLabel={collectionItems.length}
				expandedLabel={collectionItems.length}
				expanded={true}
				expandable={true}
				borderRadius
				margins={['small', '', '']}
				paddings={['small', '', '']}
				applyMaxWidth
			>
				{!isLoading && !collectionItems.length ? (
					<NoData
						applyMaxWidth
						paddings={['normal', 'normal']}
						message={i18n`You have no other **notifications**.`}
					/>
				) : null}
				<Collection
					className={styles['list']}
					ref={collectionRef}
					items={collectionItems}
					renderAhead={isBeforeTextComputation ? 0 : 10}
					itemsHeights={itemsHeights}
					cards={cards}
				/>
			</ExpandableWidget>
		</div>
	)
}

export default NotificationsView
